import 'datatables.net';

import $ from 'jquery';
import type { Ref } from 'vue';
import {
    ref,
    unref,
    watch,
} from 'vue';

import { ColVisCustomInit, InitDataTablesCustomFeatures, tabledata, tablerender } from '#public/commons-global/common';

import { type ComponentState, useRestrictedHtmlDatatable } from '../composables';
import type {
    ClickTd,
    DataTableApi,
    DataTableUpdateValues,
    FunctionAjax,
    HasChildrenHandler,
} from './data-table';
import {
    DataTableUpdateType,
} from './data-table';

export type TablePropsType = {
    id: string | undefined;
    state: ComponentState | undefined;
    processing: boolean;
    ordering: boolean;
    searching: boolean;
    search: string | undefined;
    //must be set for reactive selection to work
    isDataEqual: ((data1: unknown, data2: unknown) => boolean) | undefined;
    selectType: 'row' | 'radio' | 'checkbox' | 'checkbox-all' | undefined;
    hasChildren: HasChildrenHandler | undefined;
    columns: DataTables.Settings['columns'];
    // backward compatibility, old code called updateColumns manually
    reactiveColums: boolean | undefined;
    ajax: DataTables.FunctionAjax | FunctionAjax | undefined;
    data: unknown[] | undefined;
    initialOrder: [number, 'asc' | 'desc'][] | undefined;
    reactiveOrder: boolean | undefined;
    lengthMenu: number[] | undefined;
    customized: boolean;
    scrollX: boolean;
    chevrons: boolean;
    colvis: boolean | { exclude: number[] } | undefined;
    updateType: DataTableUpdateValues | undefined;
};

export type EventType = 'update:search'
| 'update:selection'
| 'update:initialOrder'
| 'click-td'
| 'draw'
| 'after-init'
| 'before-destroy'
| 'colvis-state-change';

export type TableEmitsType = (event: EventType, ...args: unknown[]) => void;

function orderEquals(order1: [number, 'asc' | 'desc'][] | undefined, order2: [number, 'asc' | 'desc'][] | undefined) {
    if(!order1 && !order2) {
        return true;
    }
    if(order1 && order2 && (order1.length === order2.length)) {
        for(let i = 0; i < order1.length; i++) {
            const [num1, dir1] = order1[i]!;
            const [num2, dir2] = order2[i]!;

            // Compare the number and direction at each position
            if(num1 !== num2 || dir1 !== dir2) {
                return false;
            }
        }
        return true;
    }
    return false;
}

export function useDatatable(tableRef: Ref<HTMLElement | undefined>, props: TablePropsType, emit: TableEmitsType) {
    if(props.customized) {
        InitDataTablesCustomFeatures();
    }
    if(props.colvis) {
        ColVisCustomInit();
    }

    //if table is initially frozen - first draw submitted automatically by state handler
    const state = unref(props.state);
    let off: (() => void)[] = [];
    const offUnmount: ((() => void) | undefined)[] = [];
    let search = unref(props.search) ?? '';
    let selection: unknown[] | undefined;

    function createDataTable({ frozen, ajaxInterceptor }: { frozen?: boolean; ajaxInterceptor?: Ref<FunctionAjax | undefined> } = {}) {
        const _tableRef = unref(tableRef);
        if(!_tableRef || $.fn.dataTable.isDataTable(_tableRef)) {
            return Promise.resolve();
        }
        const needCheckbox = ((props.selectType === 'radio') || (props.selectType === 'checkbox') || (props.selectType === 'checkbox-all'));
        const columns = [
            ...(props.hasChildren ? [{
                className: 'dt-control',
                orderable: false,
                data: undefined,
                defaultContent: '',
            }] : []),
            ...(needCheckbox ? [{
                title: (props.selectType === 'checkbox-all') ? '<input type="checkbox" class="select_all_none"/>' : '',
                defaultContent: '',
                render: (props.selectType === 'radio')
                    ? () => tablerender.renderRadioButton('auto')
                    : () => tablerender.renderCheckbox('auto'),
                orderable: false,
                type: 'html',
            }] : []),
            ...(props.columns || []),
        ].filter((c) => {
            if(!('skip' in c)) return true;
            const skip = c.skip;
            if(skip === true) return false;
            if(typeof (skip) === 'function') return !skip();
            return true;
        });

        const initComplete: DataTables.Settings['initComplete'] = (settings) => {
            const table0 = $(_tableRef);
            if(props.searching) {
                tabledata.bindDatatableSearchWithDelay(settings.nTable);
            }
            if(props.scrollX) {
                table0.wrap('<div class="scroll-x width-100"></div>');
            }
            if(props.selectType) {
                switch(props.selectType) {
                    case 'checkbox':
                        off.push(tabledata.bindCheckboxSelectors(table0));
                        break;
                    case 'checkbox-all':
                        {
                            const selectAll = table0.find('input.select_all_none');
                            off.push(tabledata.bindCheckboxSelectors(table0, selectAll));
                        }
                        break;
                    case 'radio':
                        off.push(tabledata.bindRadioSelectors(table0));
                        break;
                    case 'row':
                        off.push(tabledata.bindRowSelectors(table0));
                        break;
                }
            }
            let wrapper = table0.closest('.dataTables_wrapper');
            if(!wrapper.length) wrapper = table0;
            if(props.chevrons) {
                wrapper.find('.paginate_button.previous').html('').addClass('bi bi-chevron-left');
                wrapper.find('.paginate_button.next').html('').addClass('bi bi-chevron-right');
            }
            if(props.colvis) {
                $('#ColVisSelectorModal .modal-header').append($('.ColVis_Restore'));
            }

            emit('after-init', table0);
        };
        let drawComplete = false;

        const ajax: FunctionAjax = function(...args) {
            if(unref(ajaxInterceptor)) {
                unref(ajaxInterceptor)?.apply(_tableRef, args);
            } else {
                props.ajax?.apply(_tableRef, args);
            }
        };

        //ugh, new colvis doesn't like reinitializing
        if(props.colvis) {
            $('ul.ColVis_collection').empty();
            $('.ColVis_Restore').remove();
        }

        const shallExportCsv = props.customized && props.ajax;
        const toolbarClass = `${props.id ? `#${props.id}_toolbar_placeholder.` : ''}table__toolbar_placeholder`;
        const lengthClass = `${props.id ? `#${props.id}_control_length.` : ''}table__control_length`;
        const colvisClass = `${props.id ? `#${props.id}_control_colvis.` : ''}table__control_colvis`;
        const dom = `f<"${toolbarClass}">rti <"${lengthClass}"l>${shallExportCsv ? 'X' : ''}p${props.colvis ? `<"${colvisClass}"C >` : ''}`;
        const defaultOrder = [0 + (needCheckbox ? 1 : 0) + (props.hasChildren ? 1 : 0), 'desc'];
        const deferLoading = (unref(state?.frozen) || frozen);
        const table = $(_tableRef).DataTable({
            processing: props.processing,
            ordering: props.ordering,
            searching: props.searching,
            autoWidth: false,
            columns,
            order: props.initialOrder || [defaultOrder],
            //all our ajax tables are server side
            serverSide: !!props.ajax,
            ...(props.ajax ? { ajax: ajax as DataTables.FunctionAjax | undefined } : {}),
            ...(props.data ? { data: props.data } : {}),
            ...(props.searching ? { searchDelay: 1500 } : {}),
            ...((props.searching && search) ? { search: { search } } : {}),
            lengthMenu: props.lengthMenu ?? [10, 25, 50, 90],
            ...(props.colvis ? {
                oColVis: {
                    ...(props.colvis === true ? {} : props.colvis),
                    buttonText: 'colonne +/-',
                    restore: 'Predefinite',
                    label(index, title) {
                        return title;
                    },
                    stateChange(iColumn, bVisible) {
                        emit('colvis-state-change', { column: iColumn, visible: bVisible });
                    },
                },
            } : {}),
            dom,
            initComplete,
            preDrawCallback: (settings) => {
                useRestrictedHtmlDatatable(settings);
            },
            drawCallback: () => {
                const table0 = $(_tableRef);
                const dataTable = table0.DataTable();
                if(props.chevrons) {
                    let wrapper = table0.closest('.dataTables_wrapper');
                    if(!wrapper.length) wrapper = table0;
                    wrapper.find('.paginate_button.previous').html('').addClass('bi bi-chevron-left');
                    wrapper.find('.paginate_button.next').html('').addClass('bi bi-chevron-right');
                }
                const _search = dataTable.search();
                if(_search != search) {
                    search = _search;
                    emit('update:search', search);
                }
                drawComplete = true;
                emit('draw');
            },
            //deferLoading undefined is also treated as true
            ...(deferLoading ? { deferLoading: 1 } : {}),
        });

        if(props.selectType) {
            const emitSelection = () => {
                const table0 = $(_tableRef);
                switch(props.selectType) {
                    case 'checkbox':
                    case 'checkbox-all':
                    case 'radio':
                        {
                            const selected = table0.find('.table-selector:checked');
                            if(selected.length < 1) {
                                selection = undefined;
                            } else {
                                const data = $.makeArray(table.rows(selected.closest('tr')).data());
                                selection = data;
                            }
                            emit('update:selection', selection);
                        }
                        break;
                    case 'row':
                        {
                            const selected = table0.find('tr.selected');
                            if(selected.length != 1) {
                                selection = undefined;
                            } else {
                                const data = $.makeArray(table.rows(selected).data());
                                selection = data;
                            }
                            emit('update:selection', selection);
                        }
                        break;
                }
            };
            $(_tableRef).on('fe.selection-changed.vue.select', emitSelection);
        }

        $(_tableRef).on('click.vue.td', 'th,td', function(event) {
            const target = $(event.target);
            const td = $(this).closest('td');
            const tr = td.closest('tr');
            const row = table.row(tr);
            const data = row.data();
            const params: ClickTd = {
                target, td, tr, row, data, event,
            };
            emit('click-td', params);
            const hasChildren = props.hasChildren;
            if(hasChildren && td.is('.dt-control')) {
                if(row.child.isShown()) {
                    row.child.hide();
                    //tr.removeClass('shown');
                } else {
                    Promise.resolve()
                        .then(() => {
                            const child = row.child();
                            if(child) return Promise.resolve();
                            tr.addClass('dt-childLoading');
                            return hasChildren(params);
                        })
                        .then(() => {
                            //child may be still empty if caller didn't create it
                            const child = row.child();
                            if(child) {
                                row.child.show();
                                //tr.addClass('shown');
                            }
                        })
                        .catch(() => {
                        })
                        .finally(() => {
                            tr.removeClass('dt-childLoading');
                        });
                }
            }
        });

        if(props.reactiveOrder) {
            table.on('order.vue.dt', () => {
                const order = $.makeArray(table.order()) as [number, 'asc' | 'desc'][];
                if(orderEquals(order, props.initialOrder)) {
                    return;
                }
                emit('update:initialOrder', order);
            });
            off.push(() => table.off('order.vue.dt'));
        }

        if(drawComplete || deferLoading) {
            // always here, because datatables draw immediatelly
            // maybe will not be this in the future
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            table.one('draw', () => {
                resolve(undefined);
            });
        });
    }

    // no simultaneous draws
    let waitingForDraw: Promise<unknown> = Promise.resolve();

    function destroyDataTable() {
        const _tableRef = unref(tableRef);
        if(!_tableRef || !$.fn.dataTable.isDataTable(_tableRef)) {
            return;
        }
        emit('before-destroy');
        const $tableRef = $(_tableRef);
        const table = $tableRef.DataTable();
        off.forEach((fn) => fn());
        off = [];
        $tableRef.off('fe.selection-changed.vue.select');
        $tableRef.off('click.vue.td');
        table.destroy();
        $tableRef.empty();
    }

    let nextUpdateType: DataTableUpdateValues | undefined;
    function _performUpdate() {
        const _tableRef = unref(tableRef);
        if(!_tableRef) return Promise.resolve(undefined);

        //by default doing hard udpate (backward compatible)
        const updateType = nextUpdateType ?? props.updateType ?? DataTableUpdateType.Hard;

        return new Promise((resolve) => {
            nextUpdateType = undefined;
            if(updateType === DataTableUpdateType.Full) {
                destroyDataTable();
                createDataTable().then(() => { resolve(undefined); });
            } else {
                //by default doing hard udpate (backward compatible)
                const dataTable = $(_tableRef).DataTable();
                dataTable.one('draw', () => {
                    resolve(undefined);
                });
                dataTable.draw(updateType === DataTableUpdateType.Hard);
            }
        });
    }
    function performUpdate() {
        const promise = waitingForDraw.then(() => _performUpdate());
        waitingForDraw = promise;
        return promise;
    }
    offUnmount.push(state?.onUpdate(() => { performUpdate(); })?.off);

    async function _updateColumns({ keepState }: { keepState?: boolean } = {}) {
        const _tableRef = unref(tableRef);
        if(!_tableRef) return;

        let dataTable = $(_tableRef).DataTable();
        const tableState = (() => {
            if(!keepState || unref(state?.frozen) || !props.ajax) return undefined;
            return {
                search: dataTable.search(),
                page: dataTable.page(),
                pageLen: dataTable.page.len(),
                recordsTotal: dataTable.page.info().recordsTotal,
                order: dataTable.order(),
                data: $.makeArray(dataTable.data()),
            };
        })();

        // reset search for full update, otherwise restore search later
        search = '';
        const ajax = ref<FunctionAjax>();
        destroyDataTable();
        await createDataTable({ frozen: !!tableState, ajaxInterceptor: tableState ? ajax : undefined });
        if(!tableState) return;

        //here is nasty hack, we stored previous state and data, but datatables won't let us change page
        //because it has initially zero pages
        //we need to send two draw events, first to load our data and recordsTotal
        //then to redraw paging controls

        //original was destroyed
        dataTable = $(_tableRef).DataTable();
        const waitForDraw = () => new Promise((resolve) => {
            ajax.value = (data, callback) => {
                const rdata = {
                    draw: data.draw,
                    recordsTotal: tableState.recordsTotal,
                    recordsFiltered: tableState.recordsTotal,
                    data: tableState.data,
                };
                callback(rdata);
            };
            dataTable.one('draw', () => {
                ajax.value = undefined;
                resolve(undefined);
            });
            dataTable.draw(false);
        });

        await waitForDraw();
        dataTable
            .search(tableState.search)
            .page(tableState.page)
            .page.len(tableState.pageLen)
            .order(tableState.order);
        await waitForDraw();
    }
    function updateColumns(args: { keepState?: boolean } = {}) {
        const promise = waitingForDraw.then(() => _updateColumns(args));
        waitingForDraw = promise;
        return promise;
    }

    function queueUpdateType(updateType: DataTableUpdateValues) {
        if((nextUpdateType === undefined) || (updateType > nextUpdateType)) {
            nextUpdateType = updateType;
        }
    }

    const publicApi: DataTableApi = {
        queueUpdateType(update) {
            queueUpdateType(update);
        },
        performUpdate(update, forceImmediate) {
            if(update !== undefined) {
                queueUpdateType(update);
            }
            if(state && !forceImmediate) {
                state.update();
            } else {
                performUpdate();
            }
        },
        setSelection(selection0, update): Promise<undefined> {
            const selectType = props.selectType;
            const isDataEqual = props.isDataEqual;
            if(!selectType || !isDataEqual) return Promise.resolve(undefined);

            return Promise.resolve()
            .then(() => {
                if(update !== undefined) {
                    queueUpdateType(update);
                    return performUpdate();
                }
                return Promise.resolve();
            })
            .then(() => {
                const _tableRef = unref(tableRef);
                if(!_tableRef) return Promise.resolve(undefined);
                const table0 = $(_tableRef);
                const dataTable = table0.DataTable();
                const rows = table0.find('tr');
                const foundSelection: unknown[] = [];
                rows.each(function() {
                    const row = $(this);
                    const data: unknown = dataTable.row(row).data();
                    const isSelected = selection0.some((d) => isDataEqual(data, d));
                    if(isSelected) {
                        foundSelection.push(data);
                    }

                    switch(props.selectType) {
                        case 'checkbox':
                        case 'checkbox-all':
                        case 'radio':
                            {
                                const selector = row.find('.table-selector');
                                selector.prop('checked', isSelected);
                            }
                            break;
                        case 'row':
                            row.toggleClass('selected', isSelected);
                            break;
                    }
                });
                table0.find('.select_all_none').prop('checked', (foundSelection.length == rows.length));
                emit('update:selection', foundSelection);
                return Promise.resolve(undefined);
            });
        },
    };

    watch(() => props.search, () => {
        const _tableRef = unref(tableRef);
        if(!_tableRef) return;

        const _search = props.search ?? '';
        if(_search !== search) {
            search = _search;
            waitingForDraw.then(() => {
                const dataTable = $(_tableRef).DataTable();
                dataTable.search(search);
                publicApi.performUpdate();
            });
        }
    });
    watch(() => props.ajax, () => {
        publicApi.performUpdate(DataTableUpdateType.Full);
    });
    watch(() => props.data, (newData, oldData) => {
        const inplaceUpdate = (newData === oldData);
        const _tableRef = unref(tableRef);
        if(!_tableRef) {
            publicApi.performUpdate(DataTableUpdateType.Full);
            return;
        }
        waitingForDraw.then(() => {
            const dataTable = $(_tableRef).DataTable();
            dataTable.clear();
            if(newData?.length) dataTable.rows.add(newData);
            publicApi.performUpdate(inplaceUpdate ? DataTableUpdateType.Soft : DataTableUpdateType.Hard);
        });
    }, { deep: true });
    if(props.reactiveColums) {
        watch(() => props.columns, () => {
            updateColumns({ keepState: true });
        });
    } else {
        watch(() => props.columns?.map((col) => col.visible), () => {
            const _tableRef = unref(tableRef);
            if(!_tableRef) return;
            const dataTable = $(_tableRef).DataTable();
            const _columns = props.columns ?? [];
            for(let index = 0; index < _columns.length; index++) {
                dataTable.column(index).visible(_columns[index]?.visible ?? true);
            }
        });
    }
    if(props.reactiveOrder) {
        watch(() => props.initialOrder, () => {
            const _tableRef = unref(tableRef);
            if(!_tableRef) return;
            const dataTable = $(_tableRef).DataTable();
            const order = $.makeArray(dataTable.order()) as [number, 'asc' | 'desc'][];
            if(orderEquals(order, props.initialOrder)) {
                return;
            }
            dataTable.order(props.initialOrder);
            queueUpdateType(DataTableUpdateType.Hard);
            publicApi.performUpdate();
        });
    }

    return {
        createDataTable,
        destroyDataTable,
        state,
        offUnmount,
        publicApi,
    };
}
